import * as React from 'react'

type TotalItems = 7 | 5 | 4 | 1

interface Breakpoints {
  lMin: string
  mMin: string
  sMin: string
}

interface MediaQueries {
  desktop: MediaQueryList
  tablet: MediaQueryList
  mobile: MediaQueryList
}

function getBreakpoints(element: Element): Breakpoints {
  const style = getComputedStyle(element)
  return {
    lMin: style.getPropertyValue('--primitives-breakpoint-l-min').trim(),
    mMin: style.getPropertyValue('--primitives-breakpoint-m-min').trim(),
    sMin: style.getPropertyValue('--primitives-breakpoint-s-min').trim(),
  }
}

function hasAllBreakpoints({ lMin, mMin, sMin }: Breakpoints): boolean {
  return Boolean(lMin && mMin && sMin)
}

function createMediaQueries({ lMin, mMin, sMin }: Breakpoints): MediaQueries {
  return {
    desktop: window.matchMedia(`(min-width: ${lMin})`),
    tablet: window.matchMedia(`(min-width: ${mMin})`),
    mobile: window.matchMedia(`(min-width: ${sMin})`),
  }
}

function subscribeMediaQueries(mediaQueries: MediaQueries, onChange: () => void): () => void {
  mediaQueries.desktop.addEventListener('change', onChange)
  mediaQueries.tablet.addEventListener('change', onChange)
  mediaQueries.mobile.addEventListener('change', onChange)

  return () => {
    mediaQueries.desktop.removeEventListener('change', onChange)
    mediaQueries.tablet.removeEventListener('change', onChange)
    mediaQueries.mobile.removeEventListener('change', onChange)
  }
}

function computeTotalItems(mediaQueries: MediaQueries): TotalItems {
  const breakpointMap: Array<[MediaQueryList, TotalItems]> = [
    [mediaQueries.desktop, 7],
    [mediaQueries.tablet, 5],
    [mediaQueries.mobile, 4],
  ]
  return breakpointMap.find(([mq]) => mq.matches)?.[1] ?? 1
}

function resolveHostElement(containerRef?: React.RefObject<HTMLElement | null>): Element {
  return containerRef?.current ?? document.documentElement
}

export function useResponsiveTotalItems(
  override?: TotalItems,
  containerRef?: React.RefObject<HTMLElement | null>,
): TotalItems {
  const [totalItems, setTotalItems] = React.useState<TotalItems>(() => {
    if (override !== undefined)
      return override

    const element = resolveHostElement(containerRef)
    const breakpoints = getBreakpoints(element)
    if (!hasAllBreakpoints(breakpoints))
      return 7

    return computeTotalItems(createMediaQueries(breakpoints))
  })

  React.useEffect(() => {
    if (override !== undefined) {
      setTotalItems(override)
      return
    }

    const element = resolveHostElement(containerRef)

    function initFromBreakpoints(): (() => void) | null {
      const breakpoints = getBreakpoints(element)
      if (!hasAllBreakpoints(breakpoints))
        return null

      const mediaQueries = createMediaQueries(breakpoints)
      const update = () => setTotalItems(computeTotalItems(mediaQueries))

      update()

      return subscribeMediaQueries(mediaQueries, update)
    }

    let cleanupMq = initFromBreakpoints()
    if (cleanupMq)
      return cleanupMq

    // CSS vars not yet available — re-try when the theme class is applied to <html>
    const observer = new MutationObserver(() => {
      cleanupMq = initFromBreakpoints()
      if (cleanupMq)
        observer.disconnect()
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

    return () => {
      cleanupMq?.()
      observer.disconnect()
    }
  }, [override, containerRef])

  return totalItems
}
